import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { motion } from 'framer-motion';
import { EyeIcon, EyeSlashIcon, CameraIcon, UserIcon, UserGroupIcon, GlobeAltIcon } from '@heroicons/react/24/outline';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { useGoogleLogin } from '@react-oauth/google';
import { register as registerUser, googleLogin, clearError } from '../../store/slices/authSlice';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { collaborationAPI } from '../../services/api';
import toast from 'react-hot-toast';
import Logo from '../../components/common/Logo';
import EarlyAccessModal from '../../components/auth/EarlyAccessModal';
import { Capacitor } from '@capacitor/core';
import { GoogleAuth } from '@codetrix-studio/capacitor-google-auth';

const schema = yup.object({
  first_name: yup.string().required('First name is required'),
  last_name: yup.string().optional().default(''),
  email: yup.string().email('Invalid email').required('Email is required'),
  password: yup.string().min(6, 'Password must be at least 6 characters').required('Password is required'),
  confirmPassword: yup.string()
    .oneOf([yup.ref('password')], 'Passwords must match')
    .required('Please confirm your password'),
  user_type: yup.string().oneOf(['creator', 'hirer', 'model']).required('Please select your role'),
  creator_sub_type: yup.string().optional().default('photographer'),
  referral_code: yup.string().default(''),
  agree_terms: yup.boolean().oneOf([true], 'You must agree to the Privacy Policy').required('You must agree to the Privacy Policy'),
});

type RegisterFormData = yup.InferType<typeof schema>;

const RegisterPage: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('invite');
  const [inviteDetails, setInviteDetails] = useState<any>(null);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { loading, error } = useAppSelector((state) => state.auth);
  const [isEarlyAccessModalOpen, setIsEarlyAccessModalOpen] = useState(false);

  useEffect(() => {
    if (error === 'ERR_NO_EARLY_ACCESS') {
      setIsEarlyAccessModalOpen(true);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<RegisterFormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      user_type: 'creator', // Default to creator
      creator_sub_type: 'photographer',
      referral_code: '',
      agree_terms: false,
    }
  });

  const agreeTerms = watch('agree_terms');

  const googleSignupAction = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        const result = await dispatch(googleLogin({ access_token: tokenResponse.access_token }));
        if (googleLogin.fulfilled.match(result)) {
          const payload = result.payload as any;
          if (payload.is_new_user) {
            navigate('/profile/complete');
          } else {
            const redirectUrl = localStorage.getItem('redirect_after_login');
            if (redirectUrl && redirectUrl !== '/login' && redirectUrl !== '/register' && redirectUrl !== '/') {
              localStorage.removeItem('redirect_after_login');
              navigate(redirectUrl);
            } else {
              navigate('/dashboard');
            }
          }
        }
      } catch (error: any) {
        console.error('Web Google signup failed', error);
      }
    },
    onError: () => {
      // console.log('Signup Failed');
    },
  });

  const handleNativeGoogleSignup = async () => {
    try {
      await GoogleAuth.initialize({
        clientId: '393954976438-gafhoadrsao45md581r6k5nrchkoslqc.apps.googleusercontent.com',
        scopes: ['profile', 'email'],
        grantOfflineAccess: true,
      });
      const user = await GoogleAuth.signIn(); 
      // user.authentication.accessToken or idToken
      const token = user.authentication.idToken || user.authentication.accessToken;
      if (token) {
        const result = await dispatch(googleLogin({ access_token: token }));
        if (googleLogin.fulfilled.match(result)) {
          const payload = result.payload as any;
          if (payload.is_new_user) {
            navigate('/profile/complete');
          } else {
            const redirectUrl = localStorage.getItem('redirect_after_login');
            if (redirectUrl && redirectUrl !== '/login' && redirectUrl !== '/register' && redirectUrl !== '/') {
              localStorage.removeItem('redirect_after_login');
              navigate(redirectUrl);
            } else {
              navigate('/dashboard');
            }
          }
        }
      } else {
        toast.error('Native Google Signup token not received. Try again.');
      }
    } catch (error: any) {
      if (error && error.type === 'userLoggedOut') return;
      console.error('Native Google signup error:', error);
      toast.error('App Google Signup failed. Please try again.');
    }
  };

  const handleGoogleSignupClick = () => {
    if (!agreeTerms) {
      toast.error('Please agree to the Privacy Policy to continue');
      return;
    }
    if (Capacitor.isNativePlatform()) {
      handleNativeGoogleSignup();
    } else {
      googleSignupAction();
    }
  };

  const selecteduser_type = watch('user_type');

  useEffect(() => {
    if (inviteToken) {
      const fetchInviteDetails = async () => {
        try {
          const response = await collaborationAPI.getInviteDetails(inviteToken);
          if (response.data.success) {
            setInviteDetails(response.data.data);
            setValue('email', response.data.data.email);
            setValue('user_type', 'hirer'); // Default to hirer for invites, but allow change
          }
        } catch (error) {
          console.error('Failed to fetch invite details:', error);
          toast.error('Invalid or expired invite link');
        }
      };
      fetchInviteDetails();
    }
  }, [inviteToken, setValue]);

  const onSubmit = async (data: RegisterFormData) => {
    const { confirmPassword, agree_terms, ...submitData } = data;
    const finalData = {
      ...submitData,
      invite_token: inviteToken || undefined,
    };
    
    const result = await dispatch(registerUser(finalData));
    // console.log('Registration Result:', result);
    if (registerUser.fulfilled.match(result)) {
      const payload = result.payload as any;
      // console.log('Registration Payload:', payload);
      
      // Check if verification is required (no token returned)
      if (!payload.token && payload.message) {
        // toast.success('Registration successful! Please check your email to verify your account.', {
        //   duration: 6000,
        // });
        // You might want to show a specific UI state here instead of navigating
        // For now, let's navigate to login with a message or stay on page
        navigate('/login?verified=pending');
        return;
      }

      if (payload && payload.collaboration_id && payload.user?.id) {
        navigate(`/profile/${payload.user.id}/projects/${payload.collaboration_id}`);
      } else {
        navigate('/profile/complete');
      }
    }
  };

  return (
    <div className="min-h-screen flex bg-white dark:bg-gray-900">
      {/* Left Side - Hero Image */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-black">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-gray-800 to-black opacity-80 z-10"></div>
        <div className="relative z-20 flex flex-col justify-center items-center text-white p-12 h-full">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-lg"
          >
            <img src="/light-logo.png" alt="ClickCollab" className="h-16 mb-4 filter drop-shadow-md mx-auto" />
            <p className="text-xl text-gray-300 mb-12 font-light">
              Start your creative journey with thousands of professionals.
            </p>
            <div className="space-y-6 w-full">
              <div className="bg-white/5 backdrop-blur-md rounded-2xl p-6 border border-white/10 text-left hover:bg-white/10 transition-colors">
                <div className="flex items-center">
                  <div className="bg-white/10 p-3 rounded-xl mr-4">
                    <CameraIcon className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <div className="font-bold text-lg">For Creators</div>
                    <div className="text-sm text-gray-400">Showcase your portfolio and find opportunities</div>
                  </div>
                </div>
              </div>
              <div className="bg-white/5 backdrop-blur-md rounded-2xl p-6 border border-white/10 text-left hover:bg-white/10 transition-colors">
                <div className="flex items-center">
                  <div className="bg-white/10 p-3 rounded-xl mr-4">
                    <UserGroupIcon className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <div className="font-bold text-lg">For Collaborators</div>
                    <div className="text-sm text-gray-400">Join projects and connect with creative teams</div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url("/login-bg-new.png")'
          }}
        ></div>
      </div>

      {/* Right Side - Register Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 lg:p-12 overflow-y-auto">
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
          className="w-full max-w-md space-y-8"
        >
          <div className="text-center">
            <div className="flex justify-center mb-2">
              <Link to={Capacitor.isNativePlatform() ? "/register" : "/home"}>
                <Logo className="w-20 h-20 shadow-lg rounded-xl hover:opacity-90 transition-opacity cursor-pointer" />
              </Link>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white tracking-tight">Create your account</h2>
            <p className="mt-2 text-gray-500 dark:text-gray-400">Join the creative community today</p>
          </div>

          {inviteDetails && (
            <div className="bg-blue-50 border border-blue-100 rounded-2xl p-5 flex items-start">
              <div className="flex-shrink-0 bg-blue-100 rounded-full p-2">
                <UserGroupIcon className="h-5 w-5 text-blue-600" />
              </div>
              <div className="ml-4">
                <h3 className="text-sm font-bold text-blue-900">You've been invited!</h3>
                <div className="mt-1 text-sm text-blue-700 leading-relaxed">
                  You've been invited to join <strong>{inviteDetails.collaboration_title}</strong> as a <strong>{inviteDetails.role}</strong>.
                  Create your account to accept the invitation.
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* User Type Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                I am a...
              </label>
              <div className="grid grid-cols-3 gap-3">
                <label className={`
                  relative cursor-pointer rounded-xl border p-4 text-center transition-all duration-200
                  ${selecteduser_type === 'creator' 
                    ? 'border-black bg-black dark:bg-white dark:text-black text-white shadow-md transform scale-[1.02]' 
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-400'
                  }
                `}>
                  <input
                    {...register('user_type')}
                    type="radio"
                    value="creator"
                    className="sr-only"
                  />
                  <CameraIcon className={`h-6 w-6 mx-auto mb-2 ${selecteduser_type === 'creator' ? 'text-white dark:text-black' : 'text-gray-500 dark:text-gray-400'}`} />
                  <div className="font-medium text-sm">Creator</div>
                </label>

                <label className={`
                  relative cursor-pointer rounded-xl border p-4 text-center transition-all duration-200
                  ${selecteduser_type === 'model' 
                    ? 'border-black bg-black dark:bg-white dark:text-black text-white shadow-md transform scale-[1.02]' 
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-400'
                  }
                `}>
                  <input
                    {...register('user_type')}
                    type="radio"
                    value="model"
                    className="sr-only"
                  />
                  <UserIcon className={`h-6 w-6 mx-auto mb-2 ${selecteduser_type === 'model' ? 'text-white dark:text-black' : 'text-gray-500 dark:text-gray-400'}`} />
                  <div className="font-medium text-sm">Model</div>
                </label>

                <label className={`
                  relative cursor-pointer rounded-xl border p-4 text-center transition-all duration-200
                  ${selecteduser_type === 'hirer' 
                    ? 'border-black bg-black dark:bg-white dark:text-black text-white shadow-md transform scale-[1.02]' 
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-400'
                  }
                `}>
                  <input
                    {...register('user_type')}
                    type="radio"
                    value="hirer"
                    className="sr-only"
                  />
                  
                  <GlobeAltIcon className={`h-6 w-6 mx-auto mb-2 ${selecteduser_type === 'hirer' ? 'text-white dark:text-black' : 'text-gray-500 dark:text-gray-400'}`} />
                  <div className="font-medium text-sm">Hirer</div>
                </label>
              </div>
              
              {selecteduser_type === 'creator' && (
                <div className="mt-4 bg-gray-50 dark:bg-gray-800/50 p-4 rounded-xl border border-gray-150 dark:border-gray-700">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    I identify myself primarily as a...
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {['photographer', 'videographer', 'both'].map((sub) => (
                      <label key={sub} className={`
                        relative cursor-pointer rounded-lg border p-2 text-center text-xs transition-all duration-200 block
                        ${watch('creator_sub_type') === sub
                          ? 'border-black bg-black dark:bg-white dark:text-black text-white font-semibold' 
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-900'
                        }
                      `}>
                        <input
                          {...register('creator_sub_type')}
                          type="radio"
                          value={sub}
                          className="sr-only"
                        />
                        {sub === 'photographer' && 'Photographer'}
                        {sub === 'videographer' && 'Videographer'}
                        {sub === 'both' && 'Photographer & Videographer'}
                      </label>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Warning Disclaimer */}
              <div className="mt-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800/50 rounded-lg p-3 flex items-start">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-yellow-600 dark:text-yellow-500" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-yellow-800 dark:text-yellow-200 font-medium">
                    Please select your role carefully. This cannot be changed later.
                  </p>
                </div>
              </div>

              {errors.user_type && (
                <p className="mt-1.5 text-sm text-red-500">{errors.user_type.message}</p>
              )}
            </div>

            {/* Name Fields */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="first_name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  First Name
                </label>
                <Input
                  {...register('first_name')}
                  type="text"
                  placeholder="John"
                  className={errors.first_name ? 'border-red-500 focus-visible:ring-red-500' : ''}
                />
                {errors.first_name && (
                  <p className="mt-1.5 text-sm text-red-500">{errors.first_name.message}</p>
                )}
              </div>
              <div>
                <label htmlFor="last_name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Last Name
                </label>
                <Input
                  {...register('last_name')}
                  type="text"
                  placeholder="Doe"
                  className={errors.last_name ? 'border-red-500 focus-visible:ring-red-500' : ''}
                />
                {errors.last_name && (
                  <p className="mt-1.5 text-sm text-red-500">{errors.last_name.message}</p>
                )}
              </div>
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                Email Address
              </label>
              <Input
                {...register('email')}
                type="email"
                placeholder="name@example.com"
                className={`${errors.email ? 'border-red-500 focus-visible:ring-red-500' : ''} ${inviteToken ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : ''}`}
                readOnly={!!inviteToken}
              />
              {errors.email && (
                <p className="mt-1.5 text-sm text-red-500">{errors.email.message}</p>
              )}
            </div>

            {/* Password Fields */}
            <div className="space-y-4">
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Password
                </label>
                <div className="relative">
                  <Input
                    {...register('password')}
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Create a password"
                    className={errors.password ? 'border-red-500 focus-visible:ring-red-500 pr-10' : 'pr-10'}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="h-5 w-5" />
                    ) : (
                      <EyeIcon className="h-5 w-5" />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className="mt-1.5 text-sm text-red-500">{errors.password.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Confirm Password
                </label>
                <div className="relative">
                  <Input
                    {...register('confirmPassword')}
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="Confirm your password"
                    className={errors.confirmPassword ? 'border-red-500 focus-visible:ring-red-500 pr-10' : 'pr-10'}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    {showConfirmPassword ? (
                      <EyeSlashIcon className="h-5 w-5" />
                    ) : (
                      <EyeIcon className="h-5 w-5" />
                    )}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <p className="mt-1.5 text-sm text-red-500">{errors.confirmPassword.message}</p>
                )}
              </div>
            </div>

            {/* Referral Code */}
            <div>
              <label htmlFor="referral_code" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                Referral Code (Optional)
              </label>
              <Input
                {...register('referral_code')}
                type="text"
                placeholder="Enter referral code"
                className={errors.referral_code ? 'border-red-500 focus-visible:ring-red-500' : ''}
              />
              {errors.referral_code && (
                <p className="mt-1.5 text-sm text-red-500">{errors.referral_code.message}</p>
              )}
            </div>

            {/* Agreement Checkbox */}
            <div className="pt-2">
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="agree_terms"
                  {...register('agree_terms')}
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-black dark:text-white focus:ring-black dark:focus:ring-white cursor-pointer accent-black dark:accent-white flex-shrink-0"
                />
                <label htmlFor="agree_terms" className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed cursor-pointer select-none">
                  By signing up, I agree to ClickCollab's{' '}
                  <Link to="/privacy-policy" className="text-black dark:text-white font-semibold hover:underline" target="_blank" rel="noopener noreferrer">
                    Privacy Policy &amp; Terms of Service
                  </Link>.
                </label>
              </div>
              {errors.agree_terms && (
                <p className="mt-1 text-xs text-red-500 font-medium">{errors.agree_terms.message}</p>
              )}
            </div>

            <Button
              type="submit"
              size="lg"
              disabled={loading || !agreeTerms}
              className="w-full bg-black dark:bg-white hover:bg-gray-800 dark:hover:bg-gray-200 text-white dark:text-black font-bold py-6 rounded-xl shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-none"
            >
              {loading ? 'Creating Account...' : (inviteDetails ? 'Accept Invite & Join' : 'Create Account')}
            </Button>

            <div className="relative my-8">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200 dark:border-gray-700" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white dark:bg-gray-900 text-gray-500 dark:text-gray-400 font-medium">Or continue with</span>
              </div>
            </div>

            <Button
              type="button"
              variant="outline"
              size="lg"
              className="w-full py-6 rounded-xl border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-600 transition-all font-medium text-gray-700 dark:text-gray-200"
              onClick={handleGoogleSignupClick}
            >
              <img
                src="/google-icon.ico"
                alt="Google"
                className="w-5 h-5 mr-3"
              />
              Sign up with Google
            </Button>
          </form>

          <p className="text-center text-sm text-gray-600 dark:text-gray-400">
            Already have an account?{' '}
            <Link to="/login" className="font-bold text-black dark:text-white hover:underline">
              Sign in
            </Link>
          </p>
        </motion.div>
      </div>
      <EarlyAccessModal 
        isOpen={isEarlyAccessModalOpen} 
        onClose={() => setIsEarlyAccessModalOpen(false)} 
      />
    </div>
  );
};

export default RegisterPage;
